import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { baseUrl } from "../utils/api";
import { message } from "antd";

// Change from a simple Set to an object with step-specific Sets
let emailCacheByStep = {};
const token = localStorage.getItem("token");
// Create an action to reset the cache when changing steps
export const resetEmailCacheForStep = createAsyncThunk(
  "medicalAssessment/resetEmailCacheForStep",
  async (stepId, { dispatch }) => {
    // Clear the cache for this specific step
    if (emailCacheByStep[stepId]) {
      emailCacheByStep[stepId] = new Set();
    }
    return { stepId };
  },
);

// Define async thunks for API calls
export const bookMedicalAssessment = createAsyncThunk(
  "medicalAssessment/book",
  async ({ bookingData, setStep }, { rejectWithValue }) => {
    console.log(bookingData, setStep);
    try {
      const response = await axios.post(
        `${baseUrl}/medical-assessment/book`,
        bookingData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      setStep(2);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const searchProvider = createAsyncThunk(
  "medicalAssessment/searchProvider",
  async (query, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/provider/profile/search`, {
        params: { query },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// New async thunk for searching a member
export const searchMember = createAsyncThunk(
  "medicalAssessment/searchMember",
  async (name, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/member/search`, {
        params: { name },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getMembers = createAsyncThunk(
  "medicalAssessment/getMembers",
  async ({ rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/member/client?clientAccountId=100010001016`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getRegions = createAsyncThunk(
  "medicalAssessment/getRegions",
  async ({ rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/provider/profile/region-list`,

        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// Modify the checkEmailExists function to use step-specific caching
export const checkEmailExists = createAsyncThunk(
  "medicalAssessment/checkEmailExists",
  async ({ email, stepId }, { rejectWithValue, getState }) => {
    // Initialize the cache for this step if it doesn't exist
    if (!emailCacheByStep[stepId]) {
      emailCacheByStep[stepId] = new Set();
    }

    // Normalize email to lowercase to ensure consistent comparisons
    const normalizedEmail = email.toLowerCase().trim();

    // If we've already checked this email in this step and got an error, return cached result
    if (emailCacheByStep[stepId].has(normalizedEmail)) {
      const state = getState();
      const emailExists = state.medicalAssessment.emailExists;
      const emailError = state.medicalAssessment.emailCheckError;

      // If the email previously existed, show the error message again
      if (emailExists && emailError?.message) {
        message.error(emailError.message);
      }

      // Reject if email exists, otherwise succeed without API call
      return emailExists
        ? rejectWithValue({
            exists: true,
            message: emailError?.message,
            cached: true,
          })
        : { success: true, cached: true };
    }

    try {
      const response = await axios.post(
        `${baseUrl}/member/email-exists`,
        { email: normalizedEmail },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Add this email to the step-specific cache
      emailCacheByStep[stepId].add(normalizedEmail);

      return response.data;
    } catch (error) {
      // Extract the error message from the API response
      const errorMessage =
        error.response?.data?.result?.message || "Failed to check email";

      // Show the exact error message from the API
      message.error(errorMessage);

      // Add this email to the step-specific cache
      emailCacheByStep[stepId].add(normalizedEmail);

      // Return the full error response for handling in the component
      return rejectWithValue({
        exists: true,
        message: errorMessage,
        ...error.response?.data,
      });
    }
  },
);

// Create the slice
const medicalAssessmentSlice = createSlice({
  name: "medicalAssessment",
  initialState: {
    emailCheckStatus: "idle",
    emailExists: false,
    emailCheckError: null,
    emailsChecked: [],
    bookingStatus: "idle",
    bookingError: null,
    providerSearchStatus: "idle",
    providerSearchError: null,
    providers: [],
    memberSearchStatus: "idle",
    memberSearchError: null,
    members: [],
    regions: [],
    bookingData1: {},
    currentStepId: null,
  },
  reducers: {
    // Add a reducer to reset the email check cache
    resetEmailCheckCache: (state) => {
      emailCacheByStep = {}; // Reset the entire cache object
      state.emailsChecked = [];
      state.emailExists = false;
      state.emailCheckError = null;
    },
    // Add a reducer to clear a specific email from the cache
    clearEmailFromCache: (state, action) => {
      const emailToClear = action.payload.email.toLowerCase().trim();
      const stepId = action.payload.stepId;

      if (emailCacheByStep[stepId]) {
        emailCacheByStep[stepId].delete(emailToClear);
      }

      state.emailsChecked = state.emailsChecked.filter(
        (email) => email !== emailToClear,
      );
    },
    setCurrentStep: (state, action) => {
      state.currentStepId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bookMedicalAssessment.pending, (state) => {
        state.bookingStatus = "loading";
      })
      .addCase(bookMedicalAssessment.fulfilled, (state, action) => {
        state.bookingStatus = "succeeded";
        console.log(action.payload);
        state.bookingData1 = action.payload?.result;
      })
      .addCase(bookMedicalAssessment.rejected, (state, action) => {
        state.bookingStatus = "failed";
        state.bookingError = action.payload;
      })
      .addCase(searchProvider.pending, (state) => {
        state.providerSearchStatus = "loading";
      })
      .addCase(searchProvider.fulfilled, (state, action) => {
        state.providerSearchStatus = "succeeded";
        state.providers = action.payload?.result ?? [];
      })
      .addCase(searchProvider.rejected, (state, action) => {
        state.providerSearchStatus = "failed";
        state.providerSearchError = action.payload;
      })
      .addCase(searchMember.pending, (state) => {
        state.memberSearchStatus = "loading";
      })
      .addCase(searchMember.fulfilled, (state, action) => {
        state.memberSearchStatus = "succeeded";
        state.members = action.payload?.result ?? [];
      })
      .addCase(searchMember.rejected, (state, action) => {
        state.memberSearchStatus = "failed";
        state.memberSearchError = action.payload;
      })
      .addCase(getMembers.pending, (state) => {
        state.memberSearchStatus = "loading";
      })
      .addCase(getMembers.fulfilled, (state, action) => {
        state.memberSearchStatus = "succeeded";
        state.members = action.payload?.result ?? [];
      })
      .addCase(getMembers.rejected, (state, action) => {
        state.memberSearchStatus = "failed";
        state.memberSearchError = action.payload;
      })
      .addCase(getRegions.pending, (state) => {
        state.providerSearchStatus = "loading";
      })
      .addCase(getRegions.fulfilled, (state, action) => {
        state.providerSearchStatus = "succeeded";
        state.regions = action.payload?.result ?? [];
      })
      .addCase(getRegions.rejected, (state, action) => {
        state.providerSearchStatus = "failed";
        state.providerSearchError = action.payload;
      })
      .addCase(checkEmailExists.pending, (state) => {
        state.emailCheckStatus = "loading";
      })
      .addCase(checkEmailExists.fulfilled, (state, action) => {
        state.emailCheckStatus = "succeeded";
        state.emailExists = false;
        state.emailCheckError = null;

        // If this was a real API call (not cached), add to the tracked emails
        if (!action.payload?.cached) {
          const email = action.meta.arg.email.toLowerCase().trim();
          if (!state.emailsChecked.includes(email)) {
            state.emailsChecked.push(email);
          }
        }
      })
      .addCase(checkEmailExists.rejected, (state, action) => {
        state.emailCheckStatus = "failed";
        state.emailExists = true;
        state.emailCheckError = action.payload;

        // If this was a real API call (not cached), add to the tracked emails
        if (!action.payload?.cached) {
          const email = action.meta.arg.email.toLowerCase().trim();
          if (!state.emailsChecked.includes(email)) {
            state.emailsChecked.push(email);
          }
        }
      })
      .addCase(resetEmailCacheForStep.fulfilled, (state, action) => {
        state.emailExists = false;
        state.emailCheckError = null;
        state.emailCheckStatus = "idle";
      });
  },
});

// Export the actions
export const { resetEmailCheckCache, clearEmailFromCache, setCurrentStep } =
  medicalAssessmentSlice.actions;

export default medicalAssessmentSlice.reducer;
